/**
 * Subscription-related TypeScript types.
 * Mirrors the backend DTOs for type-safe API communication.
 */

/** Subscription plan available for purchase */
export interface SubscriptionPlan {
  id: number;
  name: string;
  tier: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  annualDiscountPercent: number;
  maxContacts: number | null;
  features: string[];
}

/** Current user's subscription status */
export interface SubscriptionStatus {
  isSubscribed: boolean;
  planName: string | null;
  status: string | null;
  billingInterval: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceCents: number | null;
  contactLimit: number | null;
  activeContactCount: number;
  lockedContactCount: number;
  requiresContactSelection: boolean;
  requiresOnboarding: boolean;
  /** Welcome Free vs Personal wall must block AppShell (PBI #1258). */
  requiresWelcomePlanWall: boolean;
  features: string[];
}

/** Result from creating a checkout session */
export interface CheckoutSessionResult {
  clientSecret: string | null;
  sessionId: string | null;
  mockActivated: boolean;
}

/** Request to create a checkout session */
export interface CreateCheckoutRequest {
  planId: number;
  billingInterval: string;
  /** Optional campaign slug (e.g. intro-annual); validated server-side. */
  campaignSlug?: string;
}

/** Public campaign offer display data from GET /subscription/campaigns/{slug} */
export interface SubscriptionCampaign {
  slug: string;
  isRedeemable: boolean;
  allowedBillingInterval: string;
  discountCents: number;
  /**
   * Stripe percent-off when the coupon is percentage-based; null/undefined for
   * amount-off coupons (landing page shows "Save $X" instead of "Save N%").
   */
  percentOff?: number | null;
  renewalPriceCents: number;
  introPriceCents: number;
  monthlyPriceCents: number;
  planId: number;
  planName: string;
}

/** Request to select contacts to keep (lock the rest) */
export interface ContactSelectionRequest {
  contactIdsToKeep: number[];
}

/** Billing interval constants */
export const BillingInterval = {
  Monthly: 'Monthly',
  Annual: 'Annual',
} as const;

/** Subscription feature key constants */
export const SubscriptionFeatures = {
  AutoSend: 'AutoSend',
  UnlimitedContacts: 'UnlimitedContacts',
  ContactImport: 'ContactImport',
  AIGreetingRegeneration: 'AIGreetingRegeneration',
} as const;
